import 'BomdisoftComponents/get-the-app/styles.css';
import { ActionRow, ActionRowGroup } from 'BomdisoftComponents/action-rows/index';
import type { MobileWebChoice, PhoneOs } from 'BomdisoftComponents/get-the-app/gate';
import { ChevronRightIcon } from 'BomdisoftComponents/icons/chevron-right';
import { AppleGlyph, GooglePlayGlyph } from 'BomdisoftComponents/store-badges/glyphs';
import { STORE_NAMES } from 'BomdisoftComponents/store-badges/index';
import { listingFor, type MobileAppProduct, type MobileStore } from 'BomdisoftComponents/store-badges/listings';
import {
  getTheAppDesktopRow,
  getTheAppDesktopRowMeta,
  getTheAppHeadline,
  getTheAppRemembered,
  getTheAppStoreRowAppStore,
  getTheAppStoreRowGooglePlay,
  getTheAppStoreRowMeta,
  getTheAppStoreRowSoonAppStore,
  getTheAppStoreRowSoonGooglePlay,
  getTheAppStoreRowSoonMeta,
  getTheAppSubhead,
  getTheAppSubheadUnlisted,
  getTheAppWebRow,
  getTheAppWebRowMeta,
  type SupportedLanguage,
} from '@bomdisoft/translations';
import Image from 'next/image';

export type GetTheAppProps = {
  language: SupportedLanguage;
  logoSrc: string;
  next: string;
  onChoose: (choice: MobileWebChoice) => void;
  os: PhoneOs;
  product: MobileAppProduct;
  productName: string;
  testId?: string;
};

const STORE_FOR_OS: Record<PhoneOs, MobileStore> = { android: 'googlePlay', ios: 'appStore' };

export const GetTheApp = ({ language, logoSrc, next, onChoose, os, product, productName, testId = 'get-the-app' }: GetTheAppProps) => {
  const store = STORE_FOR_OS[os];
  const url = listingFor(product, store);
  const glyph = store === 'appStore' ? <AppleGlyph /> : <GooglePlayGlyph />;
  const chevron = <ChevronRightIcon aria-hidden='true' className='bomdisoft-get-the-app-chevron' />;
  const storeLabel = url === null ? (store === 'appStore' ? getTheAppStoreRowSoonAppStore : getTheAppStoreRowSoonGooglePlay) : store === 'appStore' ? getTheAppStoreRowAppStore : getTheAppStoreRowGooglePlay;
  const storeMeta = url === null ? getTheAppStoreRowSoonMeta : getTheAppStoreRowMeta;
  const subhead = url === null ? getTheAppSubheadUnlisted[language].replace('{store}', STORE_NAMES[store]) : getTheAppSubhead[language];
  const storeRow = (
    <ActionRow
      action={url === null ? undefined : chevron}
      label={
        <span className='bomdisoft-get-the-app-row-label'>
          {glyph}
          {storeLabel[language]}
        </span>
      }
      meta={storeMeta[language]}
    />
  );

  return (
    <div className='bomdisoft-get-the-app-page' data-testid={testId}>
      <div className='bomdisoft-get-the-app-brand'>
        <Image alt='' className='bomdisoft-get-the-app-mark' height={56} src={logoSrc} width={56} />
        <span className='bomdisoft-get-the-app-name'>{productName}</span>
      </div>
      <h1 className='bomdisoft-get-the-app-headline'>{getTheAppHeadline[language].replace('{product}', productName)}</h1>
      <p className='bomdisoft-get-the-app-subhead'>{subhead}</p>
      <div className='bomdisoft-get-the-app-rows'>
        <ActionRowGroup>
          {url === null ? (
            <div aria-disabled='true' className='bomdisoft-get-the-app-row-disabled' data-testid={`${testId}-store`}>
              {storeRow}
            </div>
          ) : (
            <a className='bomdisoft-get-the-app-row-link' data-testid={`${testId}-store`} href={url} rel='noopener noreferrer' target='_blank'>
              {storeRow}
            </a>
          )}
          <a className='bomdisoft-get-the-app-row-link' data-choice='web' data-testid={`${testId}-web`} href={next} onClick={() => onChoose('web')}>
            <ActionRow action={chevron} label={getTheAppWebRow[language]} meta={getTheAppWebRowMeta[language]} />
          </a>
          <a className='bomdisoft-get-the-app-row-link' data-choice='desktop' data-testid={`${testId}-desktop`} href={next} onClick={() => onChoose('desktop')}>
            <ActionRow action={chevron} label={getTheAppDesktopRow[language]} meta={getTheAppDesktopRowMeta[language]} />
          </a>
        </ActionRowGroup>
      </div>
      <p className='bomdisoft-get-the-app-fine'>{getTheAppRemembered[language]}</p>
    </div>
  );
};
